import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import type { Action, AeDashboard } from "@rrid/shared-types";
import { useAuth } from "../lib/auth.js";
import { useCan } from "./Can.js";
import { api } from "../lib/api.js";
import { qk } from "../lib/queryKeys.js";
import { useLiveUpdates, type ConnState } from "../hooks/useLiveUpdates.js";
import { humanizeNotificationKind, userRoleSubtitle } from "../lib/rbacUi.js";
import { DevRoleSwitcher, useDevAuthAvailable } from "./DevRoleSwitcher.js";
import { ImpersonationBanner } from "./ImpersonationBanner.js";
import { UserSwitcher } from "./UserSwitcher.js";
import { Badge, Button } from "./ui.js";
import { MotdNavButton } from "../features/motd/MotdNavButton.js";

interface NotificationRow {
  id: string;
  kind: string;
  message: string;
  read: boolean;
  at: string;
}

// Access to actions *inside* each portal is enforced server-side (every write is re-checked) and
// reflected by each portal's own gating. Operations / System Status / Productivity ▾ are Central
// or Admin nav only — AEs (Managers) get the editorial portals + My Productivity. Hiding nav is
// never the enforcement (routes still authorize).
const NAV: { to: string; label: string; shortLabel?: string; action?: Action }[] = [
  // Short nav labels (drop “Portal”; pluralize the first two).
  { to: "/preprints", label: "Preprints" },
  { to: "/reviewers", label: "Reviewer Proposals", shortLabel: "Reviewers" },
  { to: "/quality-check", label: "Quality Check", shortLabel: "QC" },
  // Self-scoped; hidden for Central/Admin (they use Productivity ▾ oversight instead).
  { to: "/my-productivity", label: "Productivity" },
];

// Central/Admin tail: … Operations · Productivity ▾ · System Status (Productivity Tab v3 order).
const NAV_STAFF: { to: string; label: string; shortLabel?: string; action?: Action }[] = [
  { to: "/operations", label: "Operations", action: "openOperations" },
];
const NAV_TAIL: { to: string; label: string; shortLabel?: string; action?: Action }[] = [
  // Staff-only status board (same gate as GET /api/admin/status).
  { to: "/admin", label: "System Status", shortLabel: "Status", action: "viewAuditTrail" },
];

/** Full label at lg+; compact label below so staff strips fit narrower viewports / docs shots. */
function NavLabel({ label, shortLabel }: { label: string; shortLabel?: string }): JSX.Element {
  if (!shortLabel || shortLabel === label) return <>{label}</>;
  return (
    <>
      <span className="lg:hidden">{shortLabel}</span>
      <span className="hidden lg:inline">{label}</span>
    </>
  );
}

/** Productivity Tab v3: Central/Admin "Productivity ▾" — Team / AE overview, the Team / Student
 *  roster, and (Sep-14 re-import, WO-2026-09-14) the Preprint productivity page. AEs (Managers)
 *  use My Productivity only; this menu is oversight, not self. */
function ProductivityMenu(): JSX.Element {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = location.pathname.startsWith("/productivity");
  const item = (to: string, label: string, end = false): JSX.Element => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        clsx(
          "rounded-md px-3 py-2 text-left text-sm font-medium",
          isActive ? "bg-brand/10 text-brand" : "text-slate-700 hover:bg-slate-100",
        )
      }
    >
      {label}
    </NavLink>
  );
  return (
    <span className="relative inline-block">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium",
          active
            ? "border-brand/40 bg-brand/10 text-brand"
            : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50",
        )}
      >
        <span className="whitespace-nowrap">Productivity</span> <span className="text-[10px]">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <span className="absolute left-0 top-[calc(100%+6px)] z-40 flex min-w-56 flex-col gap-0.5 rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          {item("/productivity/students", "Team / Student productivity")}
          {item("/productivity", "Team / AE productivity", true)}
          {item("/productivity/preprints", "Preprint productivity")}
        </span>
      )}
    </span>
  );
}

function ConnPill({ state }: { state: ConnState }): JSX.Element {
  const map: Record<ConnState, { tone: "green" | "amber" | "slate"; label: string }> = {
    live: { tone: "green", label: "Live" },
    connecting: { tone: "slate", label: "Connecting…" },
    reconnecting: { tone: "amber", label: "Reconnecting…" },
  };
  const { tone, label } = map[state];
  return (
    <span className="inline-flex items-center gap-1.5" title={`Real-time connection: ${label}`}>
      <span
        className={clsx(
          "h-2 w-2 rounded-full",
          tone === "green" ? "bg-green-500" : tone === "amber" ? "bg-amber-500 animate-pulse" : "bg-slate-400",
        )}
      />
      {/* Dot-only below lg so Motd/user chrome still fits docs-width / laptop viewports. */}
      <span className="hidden text-xs text-slate-500 lg:inline">{label}</span>
    </span>
  );
}

function Notifications(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: qk.notifications,
    queryFn: () => api.get<NotificationRow[]>("/notifications"),
    refetchInterval: 20000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
  const unread = (data ?? []).filter((n) => !n.read).length;
  return (
    <div className="relative">
      <Button variant="ghost" onClick={() => setOpen((o) => !o)} aria-label={`Notifications (${unread} unread)`}>
        🔔 {unread > 0 && <Badge tone="red">{unread}</Badge>}
      </Button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 max-h-96 w-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {(data ?? []).length === 0 && <p className="p-4 text-sm text-slate-500">No notifications.</p>}
          {(data ?? []).map((n) => (
            <button
              key={n.id}
              onClick={() => markRead.mutate(n.id)}
              className={clsx("block w-full border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50", !n.read && "bg-blue-50")}
            >
              <div className="font-medium text-slate-700">{humanizeNotificationKind(n.kind)}</div>
              <div className="text-slate-600">{n.message}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface HealthPayload {
  env?: string;
  service?: string;
  sha?: string;
  buildTime?: string | null;
}

function VersionChip(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ["deploy-health"],
    queryFn: () => api.get<HealthPayload>("/health"),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  if (!data?.sha || data.sha === "dev") return null;
  const when = data.buildTime
    ? new Date(data.buildTime).toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : null;
  const label = [data.env ?? data.service, data.sha.slice(0, 7), when].filter(Boolean).join(" · ");
  return (
    <span
      className="font-mono text-[11px] text-slate-400"
      title={`Deployed service ${data.service ?? "?"} (${data.env ?? "?"})`}
    >
      {label}
    </span>
  );
}

/** AE nav cue: same unchecked total as the AE home "N proposals unchecked" badge. */
function useAeUncheckedTotal(enabled: boolean): number | null {
  const { data } = useQuery({
    queryKey: qk.dashboardAe(),
    queryFn: () => api.get<AeDashboard>("/dashboard/ae"),
    enabled,
    staleTime: 20_000,
  });
  if (!enabled || !data) return null;
  const pipeline = data.pipeline ?? [];
  return [...data.preprints.active, ...pipeline].reduce((n, c) => n + (c.uncheckedCount ?? 0), 0);
}

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout, canImpersonate } = useAuth();
  const { state } = useLiveUpdates();
  const devAuth = useDevAuthAvailable();
  const can = useCan();
  const roles = user?.roles ?? [];
  // Same Central/Admin split Home.tsx uses for staff dashboards — AEs get editorial nav only.
  const showStaffNav = roles.includes("Central") || roles.includes("Admin");
  const isAeNav = !showStaffNav && can("advanceToPitchEditorial", {});
  const uncheckedTotal = useAeUncheckedTotal(isAeNav);
  const nav = NAV.filter((n) => {
    // Self-scoped productivity is for students + AEs; Central/Admin use Productivity ▾.
    if (n.to === "/my-productivity") return !showStaffNav;
    return !n.action || can(n.action);
  }).map((n) =>
    // /my-productivity role-switches its page, and the two designs name it differently: students
    // get "My Progress" (Jul-29 student revision), AEs keep "Productivity" (AE Productivity v2).
    n.to === "/my-productivity" && !isAeNav ? { ...n, label: "My Progress" } : n,
  );
  const navStaff = showStaffNav ? NAV_STAFF.filter((n) => !n.action || can(n.action)) : [];
  const navTail = showStaffNav ? NAV_TAIL.filter((n) => !n.action || can(n.action)) : [];
  const linkClass = ({ isActive }: { isActive: boolean }): string =>
    clsx(
      "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-sm font-medium",
      isActive
        ? "border-brand/40 bg-brand/10 text-brand"
        : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50",
    );
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = (): void => {
      document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--app-header-h");
    };
  }, []);
  return (
    <div className="flex min-h-screen flex-col">
      <ImpersonationBanner />
      <header ref={headerRef} className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
          <NavLink to="/" className="text-lg font-bold tracking-tight text-brand">
            RR<span className="text-slate-400">\</span>ID
          </NavLink>
          {/* Nav labels must never break mid-label — a staff viewer has the longest strip
              (Preprints + Reviewer Proposals + Quality Check + Operations + Productivity +
              System Status). Wrapping BETWEEN links is fine. */}
          <nav className="flex min-w-0 flex-1 flex-wrap gap-x-1.5 gap-y-1.5">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass}>
                <NavLabel label={n.label} shortLabel={n.shortLabel} />
                {n.to === "/quality-check" && uncheckedTotal != null && uncheckedTotal > 0 ? (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
                    {uncheckedTotal}
                  </span>
                ) : null}
              </NavLink>
            ))}
            {/* Central/Admin only: Operations · Productivity ▾ · System Status */}
            {navStaff.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass}>
                <NavLabel label={n.label} shortLabel={n.shortLabel} />
              </NavLink>
            ))}
            {showStaffNav && <ProductivityMenu />}
            {navTail.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass}>
                <NavLabel label={n.label} shortLabel={n.shortLabel} />
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5">
            <MotdNavButton />
            <ConnPill state={state} />
            <Notifications />
            {user && (
              <div className="flex flex-col items-end gap-0.5 border-l border-slate-200 pl-3">
                <div className="flex items-center gap-2">
                  {canImpersonate ? (
                    <UserSwitcher />
                  ) : devAuth ? (
                    <DevRoleSwitcher />
                  ) : (
                    <div className="max-w-[10rem] text-right">
                      <div className="truncate text-sm font-medium leading-tight">{user.name || user.email}</div>
                      <div className="truncate text-xs text-slate-500">{userRoleSubtitle(user)}</div>
                    </div>
                  )}
                  <Button variant="secondary" onClick={() => void logout()}>
                    Sign out
                  </Button>
                </div>
                {/* Same deploy chip as the footer — sticky header at lg+; hide when cramped. */}
                <span className="hidden lg:inline-flex">
                  <VersionChip />
                </span>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">{children}</main>
      <footer className="border-t border-slate-100 py-3 text-center">
        <VersionChip />
      </footer>
    </div>
  );
}
